import { forwardRef, PropsWithChildren } from 'react'

import { css, Global, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { createPortal } from 'react-dom'

import { ArcadeModalActionProvider, ArcadeModalHeader, IsInArcadeModalContext } from '@cash-design-system/react'
import { usePreventScroll } from '@cash-design-system/react'

import { useModalSharedButtonProps } from './utils/useModalSharedProps'

export const BodyOverscrollBehavior = css`
  html,
  body {
    overscroll-behavior: none;
  }
`

const sheetEnter = keyframes`
  0% {
    transform: translateY(100%);
    border-top-left-radius: 1.5rem;
    border-top-right-radius: 1.5rem;
  }
  30% {
    transform: translateY(30%);
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
  100% {
    transform: translateY(0);
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`

const Backdrop = styled.div<{ disableAnimation: boolean }>`
  position: absolute;
  inset: 0;
  background-color: rgb(0 0 0 / 50%);
`

const IOS_SHEET_CUBIC_BEZIER = 'cubic-bezier(0.32, 0.72, 0, 1)'
const FullscreenContainer = styled.div<{ disableAnimation: boolean }>`
  z-index: 9;
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.arcadeColors.component.modalBackground};
  overflow: hidden;
  padding: 0;

  ${({ disableAnimation }) =>
    !disableAnimation &&
    css`
      animation: ${sheetEnter} 0.3s ${IOS_SHEET_CUBIC_BEZIER} forwards;
    `}
`

export const MobileSheetHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`

const ActionsContainer = styled.div`
  max-height: 4rem;

  &:has(*) {
    padding: ${({ theme }) => `${theme.spacing.xxs} ${theme.spacing.xxs} 0 ${theme.spacing.xxs}`};
  }

  @media (orientation: landscape) {
    max-height: 1rem;
  }
`

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
`

export interface MobileSheetProps {
  onClose?: () => void
  onBack?: () => void
  closable?: boolean
  disableAnimation?: boolean
}

/**
 * A WIP fullscreen sheet component for mobile web experiences.
 *
 * @remarks
 * This component is currently in development to iterate on mobile web flows,
 * specifically Card Studio. It is not intended for general use.
 */
export const MobileSheet = forwardRef<HTMLDivElement, PropsWithChildren<MobileSheetProps>>(
  ({ children, onClose, onBack, closable = true, disableAnimation = false }, ref) => {
    usePreventScroll({ prevent: true })
    const sharedModalButtonProps = useModalSharedButtonProps({
      closeButtonProps: { onClose },
      backButtonProps: { onBack },
    })

    return createPortal(
      <>
        <Global styles={BodyOverscrollBehavior} />
        <Backdrop disableAnimation={disableAnimation} />
        <FullscreenContainer
          disableAnimation={disableAnimation}
          role="dialog"
          aria-modal="true"
          data-testid="mobile-modal"
          ref={ref}
        >
          <IsInArcadeModalContext.Provider value={true}>
            <ArcadeModalActionProvider>
              <ActionsContainer>{closable ? <ArcadeModalHeader {...sharedModalButtonProps} /> : null}</ActionsContainer>
              <Content>{children}</Content>
            </ArcadeModalActionProvider>
          </IsInArcadeModalContext.Provider>
        </FullscreenContainer>
      </>,
      document.getElementById('modal-root') as HTMLElement
    )
  }
)

MobileSheet.displayName = 'MobileSheet'
